import React from 'react';
import { Box } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { colors } from '@/globalStyles';
import {
  Icon,
  Slider,
  Tooltip,
  ArticleNumberModal,
  SaveAsModal,
  SelectLanguageModal,
  Button,
  UndoIcon,
  RedoIcon,
  CopyIcon,
  PasteIcon,
  AddPageIcon,
  PrintersModal,
  FontSizeCoefModal,
  InfoIcon,
  MoreIcon,
  ArrowBackIcon,
  LaserOutputQualityModal,
  LoadCFWByArticleNumberModal,
  Label
} from '@/components';
import DeleteIcon from '@/icons/header/icon_delete.svg';
import EditSquareIcon from '@/icons/header/icon-edit-square.svg';
import {
  DeleteContextMenu,
  MaterialInfoContextMenu,
  DotsContextMenu,
  LaserOutputContextMenu,
} from './components';
import { HeaderStyled, IconWrapperStyled } from './styles';

const Header = props => {
  const {
    isMatrix,
    isDesktop,
    scaleCoef,
    isUndoAvailable,
    isRedoAvailable,
    isCopyingAvailable,
    isPastingAvailable,
    isAddPageAvailable,
    isDeleteAvailable,
    isInfoAvailable,
    isDeleteMenuVisible,
    isMatInfoVisible,
    isDebugMenuVisible,
    isDotsMenuVisible,
    isBackButtonShown,
    isArticleNumberModalOpen,
    isSaveAsModalOpen,
    isLanguageModalOpen,
    isPrinterModalOpen,
    isLaserOutputContextMenuOpen,
    isMaterialWithLaserParameters,
    isInputTypeFileSelected,
    isFontSizeCoefModalOpen,
    isLaserOutputQualityModalOpen,
    isLaserOutputQualitySelected,
    isLoadCFWByArticleNumberModalOpen,
    disableDeleteElements,
    filteredLanguage,
    laserOutputQuality,
    handleSliderChange,
    handleUndoClick,
    handleRedoClick,
    handleCopyClick,
    handlePasteClick,
    handleAddPageClick,
    handleOpenDeleteMenuClick,
    handleCloseDeleteMenu,
    handleOpenInfoMenuClick,
    handleCloseInfoMenu,
    handleOpenDotsMenuClick,
    handleCloseDotsMenu,
    handleArticleNumberModalOpen,
    handleSaveAsModalOpen,
    handleLanguageModalOpen,
    handleSetFilteredLanguage,
    handleLaserOutputClick,
    handleLaserOutputContextMenuClose,
    handleSetLaserOutputQuality,
    handlePrinterModalOpen,
    handleInputTypeFileSelection,
    handleFontSizeCoefModalOpen,
    handleLaserOutputQualityModalOpen,
    handleLoadCFWByArticleNumberModalOpen,
  } = props;

  return (
    <>
      <HeaderStyled>
        {isBackButtonShown ? (
          <Box
            sx={{ cursor: 'pointer', display: 'grid', gridTemplateColumns: 'repeat(2, max-content)', gap: '20px', alignItems: 'center' }}
          >
            <IconWrapperStyled>
              <ArrowBackIcon />
            </IconWrapperStyled>
            <Label fontSize={18} fontFamily="SourceSansPro">
              <FormattedMessage
                id="app.editor.wizard.back"
                defaultMessage="Back"
              />
            </Label>
          </Box>
        ) : (
          <Box />
        )}

        <Box />

        <Box
          sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, max-content)', gap: '50px', alignItems: 'center' }}
        >
          {isMaterialWithLaserParameters && isLaserOutputQualitySelected ? (
            <Tooltip
              open={isLaserOutputContextMenuOpen}
              placement="bottom"
              title={
                <LaserOutputContextMenu
                  handleClose={handleLaserOutputContextMenuClose}
                />
              }
              handleClose={handleLaserOutputContextMenuClose}
            >
              <Box sx={{ display: 'grid', minWidth: '325px', height: '40px' }}>
                <Button
                  variant="primary"
                  component="div"
                  onClick={handleLaserOutputClick}
                >
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr max-content',
                      alignItems: 'center',
                      gap: '15px',
                    }}
                  >
                    <Label fontFamily="SourceSansPro">
                      <span className="colon">
                        <FormattedMessage
                          id="cmme.laserOutputQuality"
                          defaultMessage="Laser output quality"
                        />
                      </span>
                      <span style={{ fontWeight: 600 }}>
                        <FormattedMessage
                          id={`cmme.laserOutputQuality.${laserOutputQuality.toLowerCase()}`}
                          defaultMessage={`cmme.laserOutputQuality.${laserOutputQuality.toLowerCase()}`}
                        />
                      </span>
                    </Label>
                    <Box sx={{ display: 'flex' }}>
                      <Icon src={EditSquareIcon} />
                    </Box>
                  </Box>
                </Button>
              </Box>
            </Tooltip>
          ) : (
            <Box />
          )}

          {isDesktop && (
            <Slider scaleCoef={scaleCoef} handleChange={handleSliderChange} />
          )}

          <Box sx={{ display: 'flex' }}>
            <Tooltip
              type="hint"
              title="app.editor.sidebar.undo"
              disabled={!isUndoAvailable}
            >
              <IconWrapperStyled
                data-value="undo"
                tabIndex={0}
                $disabled={!isUndoAvailable}
                sx={{ mr: '10px' }}
                onClick={handleUndoClick}
              >
                <UndoIcon />
              </IconWrapperStyled>
            </Tooltip>

            <Tooltip
              type="hint"
              title="app.editor.sidebar.redo"
              disabled={!isRedoAvailable}
            >
              <IconWrapperStyled
                data-value="redo"
                tabIndex={1}
                $disabled={!isRedoAvailable}
                sx={{ mr: '20px' }}
                onClick={handleRedoClick}
              >
                <RedoIcon />
              </IconWrapperStyled>
            </Tooltip>

            <Box sx={{ width: '1px', bgcolor: colors.lightGray, mr: '20px' }} />

            <Tooltip
              type="hint"
              title="app.editor.menu.autofillCopy"
              disabled={!isCopyingAvailable}
            >
              <IconWrapperStyled
                data-value="copy"
                tabIndex={2}
                $disabled={!isCopyingAvailable}
                sx={{ mr: '10px' }}
                onClick={handleCopyClick}
              >
                <CopyIcon />
              </IconWrapperStyled>
            </Tooltip>

            <Tooltip
              type="hint"
              title="app.editor.sidebar.paste"
              disabled={!isPastingAvailable}
            >
              <IconWrapperStyled
                data-value="paste"
                tabIndex={3}
                $disabled={!isPastingAvailable}
                sx={{ mr: '10px' }}
                onClick={handlePasteClick}
              >
                <PasteIcon />
              </IconWrapperStyled>
            </Tooltip>

            <Tooltip
              type="hint"
              title="cmme.addPage"
              disabled={!isAddPageAvailable}
            >
              <IconWrapperStyled
                data-value="add-page"
                tabIndex={4}
                $disabled={!isAddPageAvailable}
                sx={{ mr: '20px' }}
                onClick={handleAddPageClick}
              >
                <AddPageIcon />
              </IconWrapperStyled>
            </Tooltip>

            <Box sx={{ width: '1px', bgcolor: colors.lightGray, mr: '20px' }} />

            <Tooltip
              open={isDeleteMenuVisible}
              title={
                <DeleteContextMenu
                  isMatrix={isMatrix}
                  disableDeleteElements={disableDeleteElements}
                  handleClose={handleCloseDeleteMenu}
                />
              }
              handleClose={handleCloseDeleteMenu}
            >
              <IconWrapperStyled
                data-value="delete"
                $disabled={!isDeleteAvailable}
                sx={{ mr: '20px' }}
                onClick={handleOpenDeleteMenuClick}
              >
                <Icon src={DeleteIcon} />
              </IconWrapperStyled>
            </Tooltip>

            <Box sx={{ width: '1px', bgcolor: colors.lightGray, mr: '20px' }} />

            <Tooltip
              open={isMatInfoVisible}
              title={
                <MaterialInfoContextMenu
                  handleClose={handleCloseInfoMenu}
                />
              }
              handleClose={handleCloseInfoMenu}
            >
              <IconWrapperStyled
                data-value="info"
                $disabled={!isInfoAvailable}
                sx={{ mr: isDebugMenuVisible ? '10px' : '0px' }}
                onClick={handleOpenInfoMenuClick}
              >
                <InfoIcon />
              </IconWrapperStyled>
            </Tooltip>

            {isDebugMenuVisible && (
              <Tooltip
                open={isDotsMenuVisible}
                disabled={isInputTypeFileSelected}
                title={
                  <DotsContextMenu
                    filteredLanguage={filteredLanguage}
                    handleArticleNumberModalOpen={handleArticleNumberModalOpen}
                    handleSaveAsModalOpen={handleSaveAsModalOpen}
                    handleClose={handleCloseDotsMenu}
                    handleLanguageModalOpen={handleLanguageModalOpen}
                    handlePrinterModalOpen={handlePrinterModalOpen}
                    handleInputTypeFileSelection={handleInputTypeFileSelection}
                    handleFontSizeCoefModalOpen={handleFontSizeCoefModalOpen}
                    handleLoadCFWByArticleNumberModalOpen={
                      handleLoadCFWByArticleNumberModalOpen
                    }
                  />
                }
                handleClose={handleCloseDotsMenu}
              >
                <IconWrapperStyled
                  data-value="more"
                  onClick={handleOpenDotsMenuClick}
                >
                  <MoreIcon />
                </IconWrapperStyled>
              </Tooltip>
            )}
          </Box>
        </Box>
      </HeaderStyled>

      {isArticleNumberModalOpen && (
        <ArticleNumberModal setOpen={handleArticleNumberModalOpen} />
      )}
      {isSaveAsModalOpen && <SaveAsModal setOpen={handleSaveAsModalOpen} />}
      <SelectLanguageModal
        open={isLanguageModalOpen}
        filteredLanguage={filteredLanguage}
        setOpen={handleLanguageModalOpen}
        handleSetFilteredLanguage={handleSetFilteredLanguage}
      />
      <PrintersModal
        open={isPrinterModalOpen}
        setOpen={handlePrinterModalOpen}
      />
      {isFontSizeCoefModalOpen && (
        <FontSizeCoefModal setOpen={handleFontSizeCoefModalOpen} />
      )}
      {(isLaserOutputQualityModalOpen || !isLaserOutputQualitySelected) && (
        <LaserOutputQualityModal
          handleSetLaserOutputQuality={handleSetLaserOutputQuality}
          handleLaserOutputQualityModalOpen={handleLaserOutputQualityModalOpen}
        />
      )}
      {isLoadCFWByArticleNumberModalOpen && (
        <LoadCFWByArticleNumberModal
          setOpen={handleLoadCFWByArticleNumberModalOpen}
        />
      )}
    </>
  );
};

export default Header;
